import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { TEAM_LEAGUES_MODULE } from '@/modules/results/constants';
import { addOrRemoveLeagueToFavorites } from '@/utils/actions/leagues/addOrRemoveLeagueFavorite';
import { customRevalidateTag } from '@/utils/helpers/customRevalidateTag';

interface UseFavoriteToggleParams {
  moduleUuid: string;
  moduleFor: number;
  initialIsFavorite?: boolean;
  onRefetch?: () => void;
  isPlayerProfileLeaguesPage?: boolean;
}

interface UseFavoriteToggleResult {
  isFavorited: boolean;
  isDisabled: boolean;
  handleFavoriteToggle: (event: React.MouseEvent) => Promise<void>;
}

export function useFavoriteToggle({
  moduleUuid,
  initialIsFavorite = false,
  onRefetch,
  isPlayerProfileLeaguesPage = false,
}: UseFavoriteToggleParams): UseFavoriteToggleResult {
  const [isFavorited, setIsFavorited] = useState(initialIsFavorite);
  const [isDisabled, setIsDisabled] = useState(false);
  const queryClient = useQueryClient();

  const handleFavoriteToggle = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();
      if (isDisabled) return;
      setIsDisabled(true);

      const newFavoritedState = !isFavorited;
      setIsFavorited(newFavoritedState);

      try {
        await addOrRemoveLeagueToFavorites({
          moduleFor: 'League',
          moduleUuid,
          favoriteStatus: newFavoritedState === true ? 1 : 0,
        });
        if (isPlayerProfileLeaguesPage) {
          customRevalidateTag('player_profile_leagues');
        } else {
          queryClient.invalidateQueries({
            queryKey: ['my_leagues'],
          });
        }
      } catch {
        setIsFavorited(!newFavoritedState);
      } finally {
        setIsDisabled(false);
      }
    },
    [isDisabled, isFavorited, moduleUuid, onRefetch]
  );

  return { isFavorited, isDisabled, handleFavoriteToggle };
}

export function useLeagueFavorite(
  id: string,
  initialIsFavorite?: boolean,
  onRefetch?: () => void,
  isPlayerProfileLeaguesPage?: boolean
) {
  const { session } = useSession();

  const { isFavorited, isDisabled, handleFavoriteToggle } = useFavoriteToggle({
    moduleUuid: id,
    moduleFor: TEAM_LEAGUES_MODULE,
    initialIsFavorite,
    onRefetch,
    isPlayerProfileLeaguesPage,
  });

  return {
    isFavorite: isFavorited,
    isFavoriteDisabled: isDisabled,
    isUserLoggedIn: !!session?.user?.uuid,
    onToggle: (event: React.MouseEvent) => handleFavoriteToggle(event),
  };
}
